import {
	getInvestmentSignableWorkflowContracts,
	regenerateInvestmentSignableDocuments,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import {
	GetSignableContractsAndInvestorPhoneStatusRes,
	GetSignableContractsRes,
} from '@dealstack/shared/lib/generated';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { buildSignableDocumentsQueryKey } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/utils';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useSignableDocumentsStream } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useSignableDocumentsStream';
import { useAutoPrepareSignableDocuments } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useAutoPrepareSignableDocuments';
import { useGenerationTimeout } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useGenerationTimeout';

interface Props {
	investmentWorkflowId?: string | null;
	investorId: string;
	excludeTsaContracts: boolean;
}

export type UseSignableDocumentsResult = Partial<
	Pick<GetSignableContractsRes, 'status' | 'contracts' | 'friendlyMessage' | 'systemMessage'>
> &
	Partial<Pick<GetSignableContractsAndInvestorPhoneStatusRes, 'reviewableContracts'>> &
	Pick<GetSignableContractsAndInvestorPhoneStatusRes, 'isPhoneNumberInvalid'> & {
		isLoading: boolean;
		isError: boolean;
		/** Returns the query's promise, so callers can wait for the refreshed rows to land. */
		refetch: () => Promise<unknown>;
		generateDocuments: ({
			shouldRegenerateAllContracts,
		}: {
			shouldRegenerateAllContracts: boolean;
		}) => void;
		/** True while the SSE stream is live. Drives the "updates paused" UI. */
		isLiveUpdatesConnected: boolean;
		/** False when the browser has no EventSource — an explicit unsupported state. */
		isLiveUpdatesSupported: boolean;
		/** True when documents have sat in "preparing" too long — offer a retry. */
		isGenerationTimedOut: boolean;
		canGenerateDocuments: boolean;
	};

export const useSignableDocuments = ({
	excludeTsaContracts,
	investmentWorkflowId,
	investorId,
}: Props): UseSignableDocumentsResult => {
	const isTsaContractsRequired =
		excludeTsaContracts === false && investmentWorkflowId && !!investorId;
	const isTsaNotRequired = excludeTsaContracts === true && !!investorId;
	const isQueryEnabled = Boolean(isTsaContractsRequired || isTsaNotRequired);

	// The only refresh mechanism: the stream invalidates this query on every
	// change signal and on every reconnect. There is no polling fallback — see
	// useServerSentEvents for why reconnect-resync is what replaces it.
	const { isConnected: isSseConnected, isSupported: isSseSupported } = useSignableDocumentsStream(
		{
			investorId,
			investmentWorkflowId,
			excludeTsaContracts,
			enabled: isQueryEnabled,
		}
	);

	const {
		data: queryData,
		isLoading,
		isError,
		refetch,
	} = useQuery({
		queryKey: buildSignableDocumentsQueryKey({
			excludeTsaContracts,
			investmentWorkflowId,
			investorId,
		}),
		queryFn: ({ signal }) =>
			getInvestmentSignableWorkflowContracts(
				{
					excludeTsaContracts,
					investmentWorkflowId,
					investorId,
				},
				signal
			),
		select: (
			response: ApiV1.Response<GetSignableContractsAndInvestorPhoneStatusRes>
		): GetSignableContractsAndInvestorPhoneStatusRes | undefined => response.payload,
		enabled: isQueryEnabled,
	});

	const { mutate: regenerateSignableDocuments } = useMutation({
		mutationFn: ({ shouldRegenerateAllContracts }: { shouldRegenerateAllContracts: boolean }) =>
			regenerateInvestmentSignableDocuments({
				investorId,
				investmentWorkflowId,
				shouldRegenerateAllContracts,
			}),
		onError: () => {
			logger.error(
				`Failed to regenerate signable documents for investorId: ${investorId}, investmentWorkflowId: ${investmentWorkflowId}`
			);
			showToastV2({
				title: 'Failed to regenerate documents',
				state: 'error',
			});
		},
	});

	/**
	 * Generate documents for the investor, all wet ink but only 'per participant' 'e sign' will be generated, all_participants will need to be regenerated else where if there is an issue
	 * @param regenerate - whether to force regeneration of documents, causing any in progress documents to be cancelled and new documents to be generated.
	 */
	const generateDocuments = useCallback(
		({ shouldRegenerateAllContracts }: { shouldRegenerateAllContracts: boolean }) => {
			if (!isQueryEnabled) {
				return;
			}
			regenerateSignableDocuments(
				{ shouldRegenerateAllContracts },
				// Pull once on success rather than waiting for the generation
				// signal: preparation may legitimately have nothing to do, in
				// which case there is no state change to broadcast.
				{ onSuccess: () => refetch() }
			);
		},
		[isQueryEnabled, refetch, regenerateSignableDocuments]
	);

	const data: GetSignableContractsAndInvestorPhoneStatusRes | undefined = queryData;
	const signableContracts = data?.signableContracts;

	// Re-prepare when an SSE change flips documents back to NOT_READY (see hook below).
	const prepareDocuments = useCallback(() => {
		generateDocuments({ shouldRegenerateAllContracts: false });
	}, [generateDocuments]);

	// Unconditional now that there is no polling mode to distinguish it from:
	// the READY -> NOT_READY edge is delivered the same way whether or not the
	// connection has flapped in between.
	useAutoPrepareSignableDocuments({
		enabled: true,
		status: signableContracts?.status,
		onPrepare: prepareDocuments,
	});

	const isGenerationTimedOut = useGenerationTimeout(signableContracts?.status);

	const result: UseSignableDocumentsResult = {
		status: signableContracts?.status,
		contracts: signableContracts?.contracts,
		reviewableContracts: data?.reviewableContracts,
		friendlyMessage: signableContracts?.friendlyMessage,
		systemMessage: signableContracts?.systemMessage,
		isPhoneNumberInvalid: data?.isPhoneNumberInvalid ?? false,
		isLoading,
		isError,
		refetch,
		generateDocuments,
		isLiveUpdatesConnected: isSseConnected,
		isLiveUpdatesSupported: isSseSupported,
		isGenerationTimedOut,
		canGenerateDocuments: isQueryEnabled,
	};

	return result;
};
